import type { MenuItem as M } from 'best-modules/components/sidebar-menu/types'
import { DocumentCounts } from '@/utils/types/document'

const hasResource = (user: Record<string, any>, resource: string): boolean => {
  const permissions: string[] = user?.permissions || []
  return permissions.some(p => p.startsWith(resource + '.'))
}

type MenuItem = M & {
  defaultOpen?: boolean
  count?: number | null
  children?: MenuItem[]
}

const getDefaultMenuItems = (documentCounts: DocumentCounts): MenuItem[] => {
  return [
    {
      label: 'Дашборд',
      icon: 'intime:home',
      to: { name: 'home' },
    },
    {
      label: 'Мої документи',
      to: { name: 'document-list-me' },
      count: documentCounts.isMe,
    },
    {
      label: 'Вхідні документи',
      to: { name: 'document-list-incoming' },
    },
    {
      label: 'На реєстрації',
      icon: 'mdi-file-document-edit-outline',
      access: user => hasResource(user, 'registration'),
      children: [
        {
          label: 'На реєстрації',
          to: { name: 'document-list-on-registration' },
          count: documentCounts.onRegistration,
        },
        {
          label: 'Зареєстровані',
          to: { name: 'document-list-registered' },
          count: documentCounts.registered,
        },

        {
          label: 'Зарезервовані номери',
          to: { name: 'number-reservation-list' },
          access: user => hasResource(user, 'registration'),
        },
      ],
    },
    {
      label: 'Проекти документів',
      icon: 'intime:order',
      access: user => hasResource(user, 'documents'),
      children: [
        {
          label: 'Чернетка',
          to: { name: 'document-list-draft' },
          count: documentCounts.draft,
        },
        {
          label: 'На погодженні',
          to: { name: 'document-list-in-progress' },
          count: documentCounts.inProgress,
        },
        {
          label: 'Доопрацювання',
          to: { name: 'document-list-revision' },
          count: documentCounts.revision,
        },
      ],
    },
    {
      label: 'Мені на підпис',
      icon: 'intime:document-view',
      access: user => hasResource(user, 'documents'),
      children: [
        {
          label: 'На сьогодні (підпис)',
          to: {
            name: 'document-list-signature-today',
            count: documentCounts.signingToday,
          },
        },
        {
          label: 'Протерміновані (підпис)',
          to: {
            name: 'document-list-signature-terminated',
          },
          count: documentCounts.signingOverdue,
        },
        {
          label: 'Термін наближається (підпис)',
          to: {
            name: 'document-list-signature-terminating',
            count: documentCounts.signingApproaching,
          },
        },
      ],
    },
    {
      label: 'Мені на погодження',
      icon: 'intime:document-view',
      access: user => hasResource(user, 'documents'),
      children: [
        {
          label: 'На сьогодні (погодж.)',
          to: {
            name: 'document-list-agreement-today',
            count: documentCounts.approvalToday,
          },
        },
        {
          label: 'Протерміновані (погодж.)',
          to: {
            name: 'document-list-agreement-terminated',
            count: documentCounts.approvalOverdue,
          },
        },
        {
          label: 'Термін наближається (погодж.)',
          to: {
            name: 'document-list-agreement-terminating',
            count: documentCounts.approvalApproaching,
          },
        },
      ],
    },
    {
      label: 'Мені на розгляд',
      to: { name: 'document-list-review' },
      access: user => hasResource(user, 'documents'),
      count: documentCounts.review,
    },
    {
      label: 'Ознайомлення',
      icon: 'intime:document-view',
      access: user => hasResource(user, 'documents'),
      children: [
        {
          label: 'Мені на ознайомлення',
          to: {
            name: 'document-list-acceptance',
            query: { isAcceptedAcknowledgment: 'false' },
          },
          count: documentCounts.pendingAcknowledgment,
        },
        {
          label: 'Ознайомлені мною',
          to: {
            name: 'document-list-accepted',
            query: { isAcceptedAcknowledgment: 'true' },
          },
          count: documentCounts.acceptedAcknowledgment,
        },
      ],
    },
    {
      label: 'Контрагенти',
      icon: 'intime:contractor',
      to: { name: 'contractors' },
      access: user => hasResource(user, 'contractors'),
    },
    // TODO maybe temporary
    // {
    //   label: 'Кореспонденція',
    //   icon: 'intime:correspondence',
    //   to: { name: 'correspondence-list' },
    //   access: user => hasResource(user, 'correspondences'),
    // },
    // {
    //   label: 'Співробітники',
    //   icon: 'intime:employees',
    //   access: user => hasResource(user, 'users'),
    //   children: [
    //     {
    //       label: 'Користувачі',
    //       to: { name: 'users' },
    //     },
    //     {
    //       label: 'Структура компанії',
    //       to: { name: 'company-structure' },
    //     },
    //     {
    //       label: 'Дні народження',
    //       to: { name: 'user-birthdays' },
    //     },
    //   ],
    // },
    {
      label: 'Пошук',
      icon: 'mdi-magnify',
      to: { name: 'eds-search' },
    },
  ]
}

const operationRiskMenuItems: MenuItem[] = [
  {
    label: 'Інциденти',
    defaultOpen: true,
    access: user => hasResource(user, 'incidents'),
    children: [
      {
        label: 'Реєстр інцидентів',
        to: { name: 'incident-list' },
      },
      {
        label: 'Мої інциденти',
        to: { name: 'incident-my' },
      },
      {
        label: 'Призначені мені',
        to: { name: 'incident-assigned' },
      },
    ],
  },
  {
    label: 'Врегулювання',
    to: { name: 'settlement-list' },
    access: user => hasResource(user, 'tasks'),
  },
]

const auditMenuItems: MenuItem[] = [
  {
    label: 'Плани',
    to: { name: 'plan-list' },
  },
  {
    label: 'Аудит',
    to: { name: 'audit-list' },
    access: user => hasResource(user, 'audits'),
  },
  {
    label: 'Рекомендації',
    to: { name: 'recommendation-list' },
  },
]

const administrationMenuItems: MenuItem[] = [
  {
    label: 'Організаційна структура',
    access: user =>
      hasResource(user, 'organizations') ||
      hasResource(user, 'internal-departments') ||
      hasResource(user, 'internal-staff-units') ||
      hasResource(user, 'users') ||
      hasResource(user, 'roles'),
    children: [
      {
        label: 'Організації',
        to: { name: 'organizations' },
        access: user => hasResource(user, 'organizations'),
      },
      {
        label: 'Підрозділи',
        to: { name: 'org-internal-departments' },
        access: user => hasResource(user, 'internal-departments'),
      },
      {
        label: 'Штатний опис',
        to: { name: 'org-internal-staff-units' },
        access: user => hasResource(user, 'internal-staff-units'),
      },
      {
        label: 'Співробітники',
        to: { name: 'org-internal-employees' },
        access: user => hasResource(user, 'users'),
      },
      {
        label: 'Ролі та права',
        to: { name: 'role-list' },
        access: user => hasResource(user, 'roles'),
      },
    ],
  },
  {
    label: 'Довідники',
    access: user =>
      hasResource(user, 'org-department-types') ||
      hasResource(user, 'org-positions') ||
      hasResource(user, 'org-staff-unit-types') ||
      hasResource(user, 'org-appointment-types'),
    children: [
      {
        label: 'Довідник підрозділів',
        to: { name: 'org-department-types' },
        access: user => hasResource(user, 'org-department-types'),
      },
      {
        label: 'Довідник посад',
        to: { name: 'org-positions' },
        access: user => hasResource(user, 'org-positions'),
      },
      {
        label: 'Довідник типів штатних одиниць',
        to: { name: 'org-staff-unit-types' },
        access: user => hasResource(user, 'org-staff-unit-types'),
      },
      {
        label: 'Довідник типів призначення',
        to: { name: 'org-appointment-types' },
        access: user => hasResource(user, 'org-appointment-types'),
      },
    ],
  },
  {
    label: 'Документообіг',
    icon: 'mdi-file-document-outline',
    access: user => user?.permissions?.includes('doc-types.create'),
    children: [
      {
        label: 'Типи документів',
        to: { name: 'doc-type-definitions' },
      },
      {
        label: 'Класи документів',
        to: { name: 'doc-type-classes' },
      },
      {
        label: 'Бібліотека полів',
        to: { name: 'field-definitions' },
      },
      {
        label: 'Журнали реєстрацій',
        to: { name: 'registration-journals' },
      },
    ],
  },
  {
    label: 'Маршрутизація документів',
    to: { name: 'template-routing-document' },
    access: user => hasResource(user, 'document-templates'),
  },
]

const taskMenuItems: MenuItem[] = [
  {
    label: 'Дашборд',
    icon: 'intime:document-view',
    to: { name: 'task-dashboard' },
    access: user => hasResource(user, 'tasks'),
  },
  {
    label: 'Мої завдання',
    icon: 'intime:task',
    access: user => hasResource(user, 'tasks'),
    children: [
      {
        label: 'Очікують виконання',
        to: { name: 'incoming-tasks', query: { statusId: 1 } },
      },
      {
        label: 'В роботі',
        to: { name: 'incoming-tasks', query: { statusId: 2 } },
      },
      {
        label: 'Виконані',
        to: { name: 'incoming-tasks', query: { statusId: 3 } },
      },
      {
        label: 'Прострочені',
        to: { name: 'incoming-tasks', query: { isOverdue: 'true' } },
      },
    ],
  },
  {
    label: 'Поставлені мною',
    icon: 'intime:task',
    access: user => hasResource(user, 'tasks'),
    children: [
      {
        label: 'Активні',
        to: { name: 'outgoing-tasks', query: { statusId: 1 } },
      },
      {
        label: 'На перевірці',
        to: { name: 'outgoing-tasks', query: { statusId: 5 } },
      },
      {
        label: 'Завершені',
        to: { name: 'outgoing-tasks', query: { statusId: 3 } },
      },
      {
        label: 'Прострочені',
        to: { name: 'outgoing-tasks', query: { isOverdue: 'true' } },
      },
    ],
  },
  {
    label: 'Контролюючі',
    icon: 'intime:task',
    to: { name: 'control-tasks' },
    access: user => hasResource(user, 'tasks'),
  },
  {
    label: 'Наглядачі',
    icon: 'intime:task',
    to: { name: 'observer-tasks' },
    access: user => hasResource(user, 'tasks'),
  },
]

const documentAgreementMenuItems: MenuItem[] = [
  {
    label: 'Дашборд',
    icon: 'intime:document-view',
    to: { name: 'document-agreement-dashboard' },
    access: user => hasResource(user, 'document-agreements'),
  },
  {
    label: 'Договори',
    icon: 'intime:document-view',
    access: user => hasResource(user, 'document-agreements'),
    children: [
      {
        label: 'Нові',
        to: { name: 'document-agreement-new' },
      },
      {
        label: 'Доопрацювання',
        to: { name: 'document-agreement-revision' },
      },
      {
        label: 'Мені на візування',
        to: { name: 'document-agreement-me' },
      },
      {
        label: 'Завізовані мною',
        to: { name: 'document-agreement-agreed' },
      },
    ],
  },
]

export {
  getDefaultMenuItems,
  administrationMenuItems,
  auditMenuItems,
  operationRiskMenuItems,
  taskMenuItems,
  documentAgreementMenuItems,
}
